import React from "react";
import { createPortal } from "react-dom";
import { Main } from "./style";
import axios from "axios";
import { webURL } from "../../../services/API";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";
import {
   AlertCircle,
   ArrowLeft,
   BarChart3,
   CheckCircle2,
   ChevronLeft,
   ChevronRight,
   Clock3,
   Copy,
   FileJson,
   GitBranch,
   ListFilter,
   Play,
   RefreshCw,
   Table2,
   X,
   XCircle
} from "lucide-react";
import styled from "styled-components";
import moment from "moment";

type ExecutionStatus = "success" | "failure";
type FilterStatus = "all" | ExecutionStatus;
type OutputTab = "table" | "json";
type MobilePane = "executions" | "nodes" | "output";

interface ExecutionSummary {
   id: string;
   workflowRunId?: string;
   status: ExecutionStatus;
   registration: string;
   startedAt?: string;
   finishedAt?: string;
   durationMs?: number;
   runMode?: string;
   triggerLabel: string;
   nodeCount?: number | null;
   failedNodeCount?: number;
}

interface ExecutionNode {
   id: string;
   nodeId?: string;
   index: string;
   name: string;
   action: string;
   operation?: string;
   status: "success" | "failure" | "skipped";
   startedAt?: string;
   finishedAt?: string;
   durationMs?: number;
   output?: any;
   error?: any;
   legacy?: boolean;
}

interface ExecutionDetail extends ExecutionSummary {
   topLevelError?: any;
   nodes: ExecutionNode[];
}

interface ExecutionListResponse {
   items: ExecutionSummary[];
   summary: { total: number; success: number; failure: number };
   pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

function formatDuration(durationMs?: number) {
   if (!Number.isFinite(Number(durationMs))) return "duration unavailable";
   const ms = Number(durationMs);
   if (ms < 1000) return `${ms}ms`;
   return `${(ms / 1000).toFixed(3)}s`;
}

function formatExecutionTitle(execution?: ExecutionSummary) {
   if (!execution) return "Execution";
   const label = execution.status === "failure" ? "Error" : "Success";
   return `${label} in ${formatDuration(execution.durationMs)}`;
}

function stringifyValue(value: any) {
   if (value === undefined) return "";
   if (typeof value === "string") return value;
   try {
      return JSON.stringify(value, null, 2);
   } catch {
      return String(value);
   }
}

function parseJsonString(value: any) {
   if (typeof value !== "string") return value;
   const trimmed = value.trim();
   if (!trimmed || !["{", "["].includes(trimmed[0])) return value;
   try {
      return JSON.parse(trimmed);
   } catch {
      return value;
   }
}

function getDisplayValue(node?: ExecutionNode) {
   if (!node) return undefined;
   if (node.error && node.output === undefined) return node.error;
   return parseJsonString(node.output);
}

function isStructured(value: any) {
   return value !== null && typeof value === "object";
}

function valueToTable(value: any) {
   if (Array.isArray(value)) {
      if (!value.length) return { columns: [], rows: [] as any[] };
      const objectRows = value.every(item => item && typeof item === "object" && !Array.isArray(item));
      if (!objectRows) return null;
      const columns = Array.from(new Set(value.flatMap(item => Object.keys(item))));
      return { columns, rows: value };
   }

   if (value && typeof value === "object") {
      return { columns: Object.keys(value), rows: [value] };
   }

   return null;
}

function renderCellValue(value: any) {
   if (value === undefined) return <span className="muted">undefined</span>;
   if (value === null) return <span className="muted">null</span>;
   if (typeof value === "object") return <code>{ stringifyValue(value) }</code>;
   if (value === "") return <span className="muted">empty</span>;
   return String(value);
}

function getActionInitial(action?: string) {
   const value = String(action || "?").trim();
   return value ? value.slice(0, 2).toUpperCase() : "?";
}

export function MonitorLog({ projectId }: { projectId: string }) {
   const settingsIsRun = useSelector((state: RootState) => state.settings.data.isRunForProcess);
   const [isToggle, setIsToggle] = React.useState(false);
   const [executions, setExecutions] = React.useState<ExecutionSummary[]>([]);
   const [summary, setSummary] = React.useState({ total: 0, success: 0, failure: 0 });
   const [pagination, setPagination] = React.useState({ page: 1, pageSize: 20, total: 0, totalPages: 1 });
   const [status, setStatus] = React.useState<FilterStatus>("all");
   const [from, setFrom] = React.useState("");
   const [to, setTo] = React.useState("");
   const [selectedExecutionId, setSelectedExecutionId] = React.useState("");
   const [detail, setDetail] = React.useState<ExecutionDetail | null>(null);
   const [selectedNodeId, setSelectedNodeId] = React.useState("");
   const [outputTab, setOutputTab] = React.useState<OutputTab>("table");
   const [mobilePane, setMobilePane] = React.useState<MobilePane>("executions");
   const [isLoadingList, setIsLoadingList] = React.useState(false);
   const [isLoadingDetail, setIsLoadingDetail] = React.useState(false);
   const [error, setError] = React.useState("");

   const selectedExecution = executions.find(item => item.id === selectedExecutionId) || executions[0];
   const selectedNode = detail?.nodes.find(node => node.id === selectedNodeId) || detail?.nodes[0];
   const displayValue = getDisplayValue(selectedNode);
   const tableValue = valueToTable(displayValue);
   const hasStructuredOutput = isStructured(displayValue);

   const fetchExecutions = React.useCallback(async (options?: { preserveSelection?: boolean }) => {
      if (!projectId) return;
      setIsLoadingList(true);
      setError("");
      try {
         const params = new URLSearchParams({
            projectId,
            page: String(pagination.page),
            pageSize: String(pagination.pageSize),
         });
         if (status !== "all") params.set("status", status);
         if (from) params.set("from", from);
         if (to) params.set("to", to);

         const response = (await axios.get(`${webURL}/process/executions?${params.toString()}`)).data as ExecutionListResponse;
         setExecutions(response.items || []);
         setSummary(response.summary || { total: 0, success: 0, failure: 0 });
         setPagination(response.pagination || { page: 1, pageSize: 20, total: 0, totalPages: 1 });

         setSelectedExecutionId(previous => {
            if (options?.preserveSelection && previous && response.items.some(item => item.id === previous)) return previous;
            return response.items[0]?.id || "";
         });
      } catch (requestError: any) {
         setError(requestError?.response?.data?.message || "Failed to load workflow executions.");
      } finally {
         setIsLoadingList(false);
      }
   }, [from, pagination.page, pagination.pageSize, projectId, status, to]);

   React.useEffect(() => {
      fetchExecutions({ preserveSelection: true });
   }, [fetchExecutions, settingsIsRun]);

   React.useEffect(() => {
      if (!isToggle || !projectId || !selectedExecutionId) return;
      let isMounted = true;
      setIsLoadingDetail(true);
      setDetail(null);
      setError("");

      axios.get(`${webURL}/process/executions/${selectedExecutionId}?projectId=${projectId}`)
         .then(response => {
            if (!isMounted) return;
            const nextDetail = response.data as ExecutionDetail;
            setDetail(nextDetail);
            const firstFailed = nextDetail.nodes.find(node => node.status === "failure");
            setSelectedNodeId(previous => {
               if (previous && nextDetail.nodes.some(node => node.id === previous)) return previous;
               return (firstFailed || nextDetail.nodes[0])?.id || "";
            });
            setOutputTab("table");
            setMobilePane("nodes");
         })
         .catch(requestError => {
            if (!isMounted) return;
            setError(requestError?.response?.data?.message || "Failed to load execution details.");
         })
         .finally(() => {
            if (isMounted) setIsLoadingDetail(false);
         });

      return () => {
         isMounted = false;
      };
   }, [isToggle, projectId, selectedExecutionId]);

   React.useEffect(() => {
      if (!isToggle) return;
      const handleKeyDown = (event: KeyboardEvent) => {
         if (event.key === "Escape") setIsToggle(false);
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
   }, [isToggle]);

   const handleSelectExecution = (id: string) => {
      setSelectedExecutionId(id);
      setSelectedNodeId("");
      setMobilePane("nodes");
   };

   const handleSelectNode = (id: string) => {
      setSelectedNodeId(id);
      setMobilePane("output");
   };

   const handleCopy = async () => {
      await navigator.clipboard?.writeText(stringifyValue(displayValue));
   };

   const applyFilters = () => {
      setPagination(previous => ({ ...previous, page: 1 }));
   };

   return (
      <Main>
         <div className="boxLog" onClick={ _ => setIsToggle(true) }>
            <div className="icon"><i className="ti ti-chart-bar" aria-hidden="true" /></div>
            <div className="info">
               <span>Monitoring</span>
               <div>
                  <span><BarChart3 size="12px" color="rgb(249, 115, 22)" /> { summary.total } Total</span>
                  <span><GitBranch size="12px" color="rgb(34, 197, 94)" /> { summary.success } Success</span>
               </div>
            </div>
         </div>

         { isToggle ? createPortal(
            <Popup onClick={ _ => setIsToggle(false) }>
               <section className="inspector" onClick={ event => event.stopPropagation() } aria-label="Workflow execution inspector">
                  <header className="header">
                     <div className="titleBlock">
                        <div className="titleIcon"><BarChart3 size={ 20 } /></div>
                        <div>
                           <h3>Logs</h3>
                           <span>For current workflow</span>
                        </div>
                     </div>
                     <div className="summaryCards">
                        <span><BarChart3 size={ 14 } /> { summary.total } Total</span>
                        <span className="success"><CheckCircle2 size={ 14 } /> { summary.success } Success</span>
                        <span className="failure"><XCircle size={ 14 } /> { summary.failure } Failures</span>
                     </div>
                     <button className="iconButton" type="button" onClick={ _ => fetchExecutions({ preserveSelection: true }) } title="Refresh">
                        <RefreshCw size={ 18 } />
                     </button>
                     <button className="iconButton" type="button" onClick={ _ => setIsToggle(false) } title="Close">
                        <X size={ 19 } />
                     </button>
                  </header>

                  <div className="filters">
                     <label>
                        <ListFilter size={ 14 } />
                        <select value={ status } onChange={ event => setStatus(event.target.value as FilterStatus) }>
                           <option value="all">All statuses</option>
                           <option value="success">Success</option>
                           <option value="failure">Failure</option>
                        </select>
                     </label>
                     <label>
                        From
                        <input type="date" value={ from } onChange={ event => setFrom(event.target.value) } />
                     </label>
                     <label>
                        To
                        <input type="date" value={ to } onChange={ event => setTo(event.target.value) } />
                     </label>
                     <button type="button" onClick={ applyFilters }>Apply</button>
                  </div>

                  { error ? <div className="errorBanner"><AlertCircle size={ 16 } /> { error }</div> : null }

                  <div className="panes">
                     <aside className={ `pane history ${mobilePane === "executions" ? "activeMobile" : ""}` }>
                        <div className="paneHead">
                           <span>Executions</span>
                           { isLoadingList ? <Clock3 size={ 14 } /> : null }
                        </div>
                        <div className="scroll">
                           { executions.length ? executions.map(execution => (
                              <button
                                 key={ execution.id }
                                 type="button"
                                 className={ `executionRow ${execution.id === selectedExecutionId ? "selected" : ""}` }
                                 aria-pressed={ execution.id === selectedExecutionId }
                                 onClick={ _ => handleSelectExecution(execution.id) }
                              >
                                 <span className={ `statusDot ${execution.status}` } />
                                 <span className="rowMain">
                                    <strong>{ formatExecutionTitle(execution) }</strong>
                                    <small>{ execution.triggerLabel }</small>
                                 </span>
                                 <span className="rowMeta">
                                    <small>{ moment(execution.registration).fromNow() }</small>
                                    <small>{ execution.nodeCount ?? "?" } nodes</small>
                                 </span>
                              </button>
                           )) : <div className="emptyState">The workflow has not been run yet.</div> }
                        </div>
                        <div className="pager">
                           <button type="button" disabled={ pagination.page <= 1 } onClick={ _ => setPagination(previous => ({ ...previous, page: previous.page - 1 })) }>
                              <ChevronLeft size={ 16 } />
                           </button>
                           <span>{ pagination.page } / { pagination.totalPages }</span>
                           <button type="button" disabled={ pagination.page >= pagination.totalPages } onClick={ _ => setPagination(previous => ({ ...previous, page: previous.page + 1 })) }>
                              <ChevronRight size={ 16 } />
                           </button>
                        </div>
                     </aside>

                     <aside className={ `pane nodes ${mobilePane === "nodes" ? "activeMobile" : ""}` }>
                        <div className="paneHead">
                           <button className="backButton" type="button" onClick={ _ => setMobilePane("executions") }><ArrowLeft size={ 15 } /></button>
                           <span>{ selectedExecution ? formatExecutionTitle(selectedExecution) : "Nodes" }</span>
                        </div>
                        <div className="triggerLine">
                           <Play size={ 14 } />
                           <span>{ selectedExecution?.triggerLabel || "No execution selected" }</span>
                        </div>
                        <div className="scroll">
                           { isLoadingDetail ? <div className="emptyState">Loading nodes...</div> : null }
                           { !isLoadingDetail && detail?.topLevelError ? (
                              <div className="topError">
                                 <XCircle size={ 15 } />
                                 <span>{ stringifyValue(detail.topLevelError) }</span>
                              </div>
                           ) : null }
                           { !isLoadingDetail && detail?.nodes.length ? detail.nodes.map(node => (
                              <button
                                 key={ node.id }
                                 type="button"
                                 className={ `nodeRow ${node.id === selectedNodeId ? "selected" : ""}` }
                                 aria-pressed={ node.id === selectedNodeId }
                                 onClick={ _ => handleSelectNode(node.id) }
                              >
                                 <span className="nodeIcon">{ getActionInitial(node.action) }</span>
                                 <span className="rowMain">
                                    <strong>{ node.name || node.index }</strong>
                                    <small>{ node.operation || node.action || "operation unavailable" }</small>
                                 </span>
                                 <span className={ `nodeStatus ${node.status}` }>{ node.status }</span>
                                 <small>{ formatDuration(node.durationMs) }</small>
                              </button>
                           )) : null }
                           { !isLoadingDetail && detail && !detail.nodes.length ? <div className="emptyState">No node output was saved for this execution.</div> : null }
                        </div>
                     </aside>

                     <section className={ `pane output ${mobilePane === "output" ? "activeMobile" : ""}` }>
                        <div className="paneHead">
                           <button className="backButton" type="button" onClick={ _ => setMobilePane("nodes") }><ArrowLeft size={ 15 } /></button>
                           <span>{ selectedNode?.name || "Output" }</span>
                           <div className="tabs">
                              <button type="button" className={ outputTab === "table" ? "active" : "" } onClick={ _ => setOutputTab("table") }><Table2 size={ 14 } /> Table</button>
                              <button type="button" className={ outputTab === "json" ? "active" : "" } onClick={ _ => setOutputTab("json") }><FileJson size={ 14 } /> JSON</button>
                           </div>
                           <button className="iconButton small" type="button" onClick={ handleCopy } title="Copy output" disabled={ displayValue === undefined }>
                              <Copy size={ 15 } />
                           </button>
                        </div>

                        <div className="outputBody">
                           { !selectedNode ? <div className="emptyState">Select a node to inspect its output.</div> : null }
                           { selectedNode && displayValue === undefined ? <div className="emptyState">This node completed without a saved output.</div> : null }
                           { selectedNode && displayValue === null ? <div className="emptyState">This node returned null.</div> : null }
                           { selectedNode && displayValue !== undefined && displayValue !== null && outputTab === "table" ? (
                              tableValue ? (
                                 tableValue.columns.length ? (
                                    <div className="tableWrap">
                                       <table>
                                          <thead>
                                             <tr>{ tableValue.columns.map(column => <th key={ column }>{ column }</th>) }</tr>
                                          </thead>
                                          <tbody>
                                             { tableValue.rows.map((row, rowIndex) => (
                                                <tr key={ rowIndex.toString() }>
                                                   { tableValue.columns.map(column => <td key={ column }>{ renderCellValue(row[column]) }</td>) }
                                                </tr>
                                             )) }
                                          </tbody>
                                       </table>
                                    </div>
                                 ) : <div className="emptyState">This array is empty.</div>
                              ) : (
                                 <pre className={ hasStructuredOutput ? "jsonPre" : "textPre" }>{ stringifyValue(displayValue) || "empty" }</pre>
                              )
                           ) : null }
                           { selectedNode && displayValue !== undefined && displayValue !== null && outputTab === "json" ? (
                              <pre className="jsonPre">{ stringifyValue(displayValue) || "empty" }</pre>
                           ) : null }
                        </div>
                     </section>
                  </div>
               </section>
            </Popup>,
            document.body
         ) : null }
      </Main>
   );
}

const Popup = styled.div`
   position: fixed;
   inset: 0;
   display: flex;
   align-items: center;
   justify-content: center;
   background: rgba(15, 23, 42, 0.62);
   backdrop-filter: blur(3px);
   z-index: 10000;
   padding: 24px;

   * {
      box-sizing: border-box;
   }

   .inspector {
      width: min(1120px, calc(100vw - 48px));
      height: min(680px, calc(100vh - 48px));
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border: 1px solid rgba(148, 163, 184, 0.45);
      border-radius: 12px;
      background: #f8fafc;
      color: #0f172a;
      box-shadow: 0 24px 80px rgba(15, 23, 42, 0.28);
   }

   .header {
      display: grid;
      grid-template-columns: 1fr auto auto auto;
      align-items: center;
      gap: 12px;
      min-height: 68px;
      padding: 14px 18px;
      border-bottom: 1px solid #e2e8f0;
      background: #fff;
   }

   .titleBlock, .summaryCards, .filters, .paneHead, .triggerLine, .errorBanner, .topError {
      display: flex;
      align-items: center;
   }

   .titleBlock {
      gap: 10px;

      h3 {
         margin: 0;
         font-size: 18px;
         line-height: 1.1;
         font-weight: 650;
      }

      span {
         display: block;
         margin-top: 3px;
         color: #64748b;
         font-size: 12px;
      }
   }

   .titleIcon {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      background: #fff7ed;
      color: #ea580c;
      border: 1px solid #fed7aa;
   }

   .summaryCards {
      gap: 8px;

      span {
         height: 30px;
         display: inline-flex;
         align-items: center;
         gap: 6px;
         padding: 0 10px;
         border: 1px solid #e2e8f0;
         border-radius: 6px;
         background: #fff;
         color: #475569;
         font-size: 12px;
         font-weight: 600;
      }

      .success {
         color: #15803d;
         border-color: #bbf7d0;
         background: #f0fdf4;
      }

      .failure {
         color: #b91c1c;
         border-color: #fecaca;
         background: #fef2f2;
      }
   }

   .iconButton, .pager button, .backButton {
      border: 1px solid #e2e8f0;
      background: #fff;
      color: #475569;
      border-radius: 6px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      &:hover:not(:disabled) {
         background: #f8fafc;
         color: #0f172a;
      }

      &:disabled {
         cursor: not-allowed;
         opacity: 0.45;
      }
   }

   .iconButton {
      width: 34px;
      height: 34px;

      &.small {
         width: 30px;
         height: 30px;
      }
   }

   .filters {
      gap: 8px;
      padding: 10px 18px;
      border-bottom: 1px solid #e2e8f0;
      background: #fff;

      label {
         height: 34px;
         display: inline-flex;
         align-items: center;
         gap: 7px;
         border: 1px solid #e2e8f0;
         border-radius: 6px;
         background: #fff;
         color: #64748b;
         padding: 0 10px;
         font-size: 12px;
         font-weight: 600;
      }

      select, input {
         border: 0;
         outline: 0;
         background: transparent;
         color: #0f172a;
         font: inherit;
      }

      button {
         height: 34px;
         border: 0;
         border-radius: 6px;
         padding: 0 14px;
         background: #0f172a;
         color: #fff;
         font-size: 12px;
         font-weight: 700;
         cursor: pointer;
      }
   }

   .errorBanner {
      gap: 8px;
      margin: 10px 18px 0;
      padding: 10px 12px;
      border: 1px solid #fecaca;
      border-radius: 6px;
      background: #fef2f2;
      color: #b91c1c;
      font-size: 13px;
   }

   .panes {
      min-height: 0;
      flex: 1;
      display: grid;
      grid-template-columns: 340px 360px minmax(0, 1fr);
      gap: 1px;
      background: #e2e8f0;
      border-top: 1px solid #e2e8f0;
   }

   .pane {
      min-width: 0;
      min-height: 0;
      display: flex;
      flex-direction: column;
      background: #fff;
   }

   .paneHead {
      min-height: 48px;
      gap: 8px;
      padding: 0 14px;
      border-bottom: 1px solid #e2e8f0;
      font-size: 13px;
      font-weight: 700;

      > span {
         min-width: 0;
         overflow: hidden;
         text-overflow: ellipsis;
         white-space: nowrap;
      }
   }

   .backButton {
      display: none;
      width: 28px;
      height: 28px;
      flex: 0 0 auto;
   }

   .scroll, .outputBody {
      min-height: 0;
      flex: 1;
      overflow: auto;
      padding: 10px;
   }

   .executionRow, .nodeRow {
      width: 100%;
      display: grid;
      align-items: center;
      gap: 10px;
      border: 1px solid transparent;
      background: transparent;
      border-radius: 6px;
      color: #0f172a;
      text-align: left;
      cursor: pointer;

      &:hover {
         background: #f8fafc;
      }

      &.selected {
         border-color: #fdba74;
         background: #fff7ed;
      }
   }

   .executionRow {
      grid-template-columns: 10px minmax(0, 1fr) auto;
      padding: 10px;
   }

   .nodeRow {
      grid-template-columns: 34px minmax(0, 1fr) auto auto;
      padding: 9px;
   }

   .statusDot {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background: #22c55e;

      &.failure {
         background: #ef4444;
      }
   }

   .rowMain {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 3px;

      strong {
         min-width: 0;
         overflow: hidden;
         text-overflow: ellipsis;
         white-space: nowrap;
         font-size: 13px;
         font-weight: 700;
      }

      small {
         min-width: 0;
         overflow: hidden;
         text-overflow: ellipsis;
         white-space: nowrap;
         color: #64748b;
         font-size: 11px;
      }
   }

   .rowMeta {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 3px;
      color: #64748b;
      font-size: 11px;
   }

   .nodeIcon {
      width: 32px;
      height: 32px;
      border-radius: 7px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      color: #334155;
      font-size: 10px;
      font-weight: 800;
   }

   .nodeStatus {
      min-width: 58px;
      height: 22px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 700;
      text-transform: capitalize;

      &.success {
         color: #15803d;
         background: #dcfce7;
      }

      &.failure {
         color: #b91c1c;
         background: #fee2e2;
      }

      &.skipped {
         color: #475569;
         background: #e2e8f0;
      }
   }

   .triggerLine {
      gap: 7px;
      padding: 10px 14px;
      border-bottom: 1px solid #e2e8f0;
      color: #64748b;
      font-size: 12px;
   }

   .topError {
      gap: 7px;
      align-items: flex-start;
      margin-bottom: 10px;
      padding: 10px;
      border-radius: 6px;
      border: 1px solid #fecaca;
      background: #fef2f2;
      color: #b91c1c;
      font-size: 12px;

      span {
         min-width: 0;
         white-space: pre-wrap;
         word-break: break-word;
      }
   }

   .pager {
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      border-top: 1px solid #e2e8f0;
      color: #64748b;
      font-size: 12px;
      font-weight: 700;

      button {
         width: 28px;
         height: 28px;
      }
   }

   .tabs {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      overflow: hidden;

      button {
         height: 30px;
         display: inline-flex;
         align-items: center;
         gap: 5px;
         border: 0;
         padding: 0 10px;
         background: #fff;
         color: #64748b;
         font-size: 12px;
         font-weight: 700;
         cursor: pointer;

         &.active {
            background: #0f172a;
            color: #fff;
         }
      }
   }

   .outputBody {
      background: #f8fafc;
   }

   .tableWrap {
      max-width: 100%;
      overflow: auto;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      background: #fff;
   }

   table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
   }

   th, td {
      min-width: 140px;
      max-width: 340px;
      padding: 10px;
      border-bottom: 1px solid #e2e8f0;
      vertical-align: top;
      text-align: left;
   }

   th {
      position: sticky;
      top: 0;
      z-index: 1;
      background: #f1f5f9;
      color: #334155;
      font-size: 11px;
      text-transform: none;
   }

   td {
      color: #0f172a;
      word-break: break-word;

      code {
         white-space: pre-wrap;
         font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
         font-size: 11px;
      }
   }

   .jsonPre, .textPre {
      min-height: 100%;
      margin: 0;
      padding: 14px;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      background: #0f172a;
      color: #e2e8f0;
      white-space: pre-wrap;
      word-break: break-word;
      font-size: 12px;
      line-height: 1.5;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
   }

   .textPre {
      background: #fff;
      color: #0f172a;
   }

   .emptyState {
      min-height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 20px;
      color: #64748b;
      font-size: 13px;
   }

   .muted {
      color: #94a3b8;
      font-style: italic;
   }

   @media (max-width: 980px) {
      padding: 0;

      .inspector {
         width: 100%;
         height: 100vh;
         border-radius: 0;
      }

      .header {
         grid-template-columns: 1fr auto auto;
      }

      .summaryCards {
         display: none;
      }

      .filters {
         overflow-x: auto;
      }

      .panes {
         display: block;
         background: #fff;
      }

      .pane {
         display: none;
         height: 100%;
      }

      .pane.activeMobile {
         display: flex;
      }

      .backButton {
         display: inline-flex;
      }

      .history .backButton {
         display: none;
      }
   }
`;
